/**
 * Der Zustand des Spiels im Client.
 *
 * Zwei Regeln:
 *
 * 1. **Der Server hat recht.** Beim Tippen setzt der Client ein Feld sofort
 *    auf „prüfe", damit die Anzeige nicht auf das Netz wartet (FR-MARK-01,
 *    100 ms). Jede Bewertung danach kommt vom Server und wird nicht
 *    gerechnet. Was hier steht, ist eine Anzeige und keine zweite Wahrheit.
 * 2. **Kein Tipp geht verloren.** Jeder Tipp landet erst in der
 *    Warteschlange, dann auf der Leitung.
 */
import { create } from 'zustand'

import { api, setCurrentRoom } from './api'
import { Live, type LiveState } from './live'
import * as queue from './queue'
import type { BingoLine, Board, CellStatus, LineState, Question, RoomState, Term } from './types'

interface StatusPayload {
  cells?: CellStatus[]
  lines?: LineState[]
  bingos?: BingoLine[]
  oneAway?: number[]
  questions?: Question[]
  room?: RoomState
}

export interface Toast {
  id: number
  text: string
  kind: 'bingo' | 'info' | 'error'
}

interface GameState {
  roomId: string | null
  room: RoomState | null
  cells: string[]
  terms: Record<string, Term>
  status: Record<number, CellStatus>
  lines: LineState[]
  bingos: BingoLine[]
  oneAway: number[]
  questions: Question[]
  board: Board | null
  pulse: number[]
  marks2min: number
  live: LiveState
  queued: number
  toasts: Toast[]
  flash: number[]
  error: string | null

  enter: (roomId: string, opts?: { gm?: boolean }) => Promise<void>
  leave: () => void
  toggle: (cell: number) => Promise<void>
  answer: (markId: string, keep: boolean) => Promise<void>
  refresh: () => Promise<void>
  loadBoard: () => Promise<void>
  reshuffle: () => Promise<void>
  flush: () => Promise<void>
  toast: (text: string, kind?: Toast['kind']) => void
  dismissToast: (id: number) => void
  setError: (message: string | null) => void
}

let live: Live | null = null
let toastSeq = 0

function indexStatus(cells: CellStatus[] | undefined): Record<number, CellStatus> {
  const out: Record<number, CellStatus> = {}
  for (const c of cells ?? []) out[c.cell] = c
  return out
}

export const useGame = create<GameState>((set, get) => ({
  roomId: null,
  room: null,
  cells: [],
  terms: {},
  status: {},
  lines: [],
  bingos: [],
  oneAway: [],
  questions: [],
  board: null,
  pulse: new Array(12).fill(0),
  marks2min: 0,
  live: 'offline',
  queued: 0,
  toasts: [],
  flash: [],
  error: null,

  toast: (text, kind = 'info') => {
    const id = ++toastSeq
    set((s) => ({ toasts: [...s.toasts, { id, text, kind }] }))
    window.setTimeout(() => get().dismissToast(id), kind === 'bingo' ? 5000 : 3500)
  },
  dismissToast: (id) => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
  setError: (message) => set({ error: message }),

  async enter(roomId, opts) {
    setCurrentRoom(roomId)
    set({ roomId, error: null })

    const joined = await api.post<{ room: RoomState; card: { cells: string[] } }>(
      `/rooms/${roomId}/join`,
    )
    const card = await api.get<{ cells: string[]; terms: Record<string, Term> }>(
      `/rooms/${roomId}/card`,
    )
    set({ room: joined.room, cells: card.cells, terms: card.terms })

    await get().refresh()
    await get().flush()

    const token = await api.post<{ mqtt_token: string; mqtt_url: string }>('/auth/mqtt-token', {
      room_id: roomId,
      roles: opts?.gm ? ['player', 'game_master'] : ['player'],
    })
    const session = JSON.parse(localStorage.getItem('bsb.session') ?? '{}') as { session_id?: string }

    live?.disconnect()
    live = new Live(
      (topic, payload) => {
        if (topic.endsWith('/status')) {
          const p = payload as StatusPayload
          const next = indexStatus(p.cells)
          const before = get().status
          const changed = Object.values(next)
            .filter((c) => before[c.cell]?.grade !== c.grade && ['A1', 'B2', 'C3'].includes(c.grade))
            .map((c) => c.cell)
          set({
            status: next,
            lines: p.lines ?? get().lines,
            bingos: p.bingos ?? get().bingos,
            oneAway: p.oneAway ?? get().oneAway,
            questions: p.questions ?? get().questions,
            flash: changed,
          })
          if (changed.length) buzz()
        } else if (topic.endsWith('/board')) {
          set({ board: payload as unknown as Board })
        } else if (topic.endsWith('/pulse')) {
          const p = payload as { marks2min?: number; buckets?: number[] }
          set({ pulse: p.buckets ?? get().pulse, marks2min: p.marks2min ?? 0 })
        } else if (topic.endsWith('/state')) {
          set({ room: payload as unknown as RoomState })
        } else if (topic.endsWith('/events')) {
          const p = payload as { type?: string; name?: string; text?: string }
          if (p.type === 'bingo') get().toast(`${p.name}: Bingo`, 'bingo')
          if (p.type === 'announce' && p.text) get().toast(p.text)
        } else if (topic.endsWith('/notice')) {
          const p = payload as { type?: string; message?: string; approved?: boolean }
          if (p.type === 'bingo_decision') {
            get().toast(p.approved ? 'Bingo freigegeben' : 'Bingo abgelehnt', p.approved ? 'bingo' : 'error')
          } else if (p.message) {
            get().toast(p.message, 'error')
          }
        }
      },
      (state) => set({ live: state }),
      async () => {
        await get().refresh()
      },
    )
    await live.connect({
      url: token.mqtt_url,
      token: token.mqtt_token,
      roomId,
      sessionId: session.session_id ?? '',
      gm: opts?.gm,
    })
  },

  leave() {
    live?.disconnect()
    live = null
    setCurrentRoom(null)
    set({
      roomId: null, room: null, cells: [], status: {}, lines: [], bingos: [],
      oneAway: [], questions: [], board: null, flash: [],
    })
  },

  async refresh() {
    const roomId = get().roomId
    if (!roomId) return
    const p = await api.get<StatusPayload>(`/rooms/${roomId}/status`)
    set({
      room: p.room ?? get().room,
      status: indexStatus(p.cells),
      lines: p.lines ?? [],
      bingos: p.bingos ?? [],
      oneAway: p.oneAway ?? [],
      questions: p.questions ?? [],
    })
  },

  async toggle(cell) {
    const { roomId, cells, status } = get()
    const termId = cells[cell]
    if (!roomId || !termId || cell === 12) return

    const existing = status[cell]
    const action = existing ? 'unmark' : 'mark'
    const markId = queue.newId()

    // Sofort sichtbar, unabhaengig vom Netz.
    set((s) => {
      const next = { ...s.status }
      if (action === 'unmark') delete next[cell]
      else next[cell] = { markId, cell, termId, grade: 'pend', sources: 0, final: false }
      return { status: next }
    })

    await queue.enqueue({
      mark_id: markId,
      term_id: termId,
      action,
      client_ts: new Date().toISOString(),
      room_id: roomId,
      queued: true,
    })
    set({ queued: await queue.count(roomId) })

    const viaBroker = live?.publish(roomId, sessionId(), 'mark', {
      markId, termId, action, clientTs: new Date().toISOString(),
    })
    if (viaBroker) {
      // Der Broker hat sie. Aus der Warteschlange raus, sobald der Server
      // den Stand zurueckmeldet; bis dahin bleibt sie als Sicherung liegen.
      window.setTimeout(() => void get().flush(), 1200)
    } else {
      await get().flush()
    }
  },

  async flush() {
    const roomId = get().roomId
    if (!roomId) return
    const items = await queue.pending(roomId)
    if (!items.length) {
      set({ queued: 0 })
      return
    }
    try {
      const res = await api.post<StatusPayload>(`/rooms/${roomId}/marks:batch`, {
        marks: items.map((q) => ({
          mark_id: q.mark_id,
          term_id: q.term_id,
          action: q.action,
          client_ts: q.client_ts,
          queued: true,
        })),
      })
      await queue.drop(items.map((q) => q.mark_id))
      set({
        queued: await queue.count(roomId),
        status: indexStatus(res.cells),
        lines: res.lines ?? get().lines,
        bingos: res.bingos ?? get().bingos,
        oneAway: res.oneAway ?? get().oneAway,
      })
    } catch (e) {
      // Bleibt liegen. Beim naechsten Mal nochmal.
      set({ queued: await queue.count(roomId) })
      const code = (e as { code?: string }).code
      if (code && code !== 'offline' && code !== 'rate_limited') get().setError(code)
    }
  },

  async answer(markId, keep) {
    const roomId = get().roomId
    if (!roomId) return
    set((s) => ({ questions: s.questions.filter((q) => q.markId !== markId) }))
    if (!live?.publish(roomId, sessionId(), 'answer', { markId, keep })) {
      await api.post(`/rooms/${roomId}/answer`, { mark_id: markId, keep })
    }
    await get().refresh()
  },

  /** Eine neue Karte aus demselben Set (FR-ROOM-07).
   *
   *  In einer Uebungsrunde beliebig oft: dort gibt es nichts zu gewinnen,
   *  und 54 Floskeln passen nicht auf 24 Felder.
   */
  async reshuffle() {
    const roomId = get().roomId
    if (!roomId) return
    try {
      const res = await api.post<{ cells: string[] }>(`/rooms/${roomId}/card/reroll`)
      set({ cells: res.cells, status: {}, lines: [], bingos: [], oneAway: [], flash: [] })
      const card = await api.get<{ terms: Record<string, Term> }>(`/rooms/${roomId}/card`)
      set({ terms: card.terms })
    } catch (e) {
      get().setError((e as { code?: string }).code ?? 'generic')
    }
  },

  async loadBoard() {
    const roomId = get().roomId
    if (!roomId) return
    set({ board: await api.get<Board>(`/rooms/${roomId}/board`) })
  },
}))

function sessionId(): string {
  try {
    return (JSON.parse(localStorage.getItem('bsb.session') ?? '{}') as { session_id?: string }).session_id ?? ''
  } catch {
    return ''
  }
}

function buzz(): void {
  // FR-MARK-04, und beides respektiert die Systemeinstellung: ohne
  // Vibrations-API passiert nichts, und wer Bewegung reduziert hat, bekommt
  // auch kein Rütteln.
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
  if ('vibrate' in navigator) navigator.vibrate(30)
}

/** Nachreichen, sobald das Netz zurueck ist. */
export function watchConnectivity(): () => void {
  const onOnline = () => {
    void useGame.getState().flush()
  }
  window.addEventListener('online', onOnline)
  return () => window.removeEventListener('online', onOnline)
}
